import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import type { InvoiceCalculations } from "@/hooks/use-invoice-calculations";
import { AdHocInterimDialog } from "./AdHocInterimDialog";
import type { InvoicePrefill } from "./CreateInvoiceForm";

interface InterimInvoiceSectionProps {
  projectName: string;
  calc: InvoiceCalculations;
  /** Neste planlagte mellomfaktura kan foreslås (oppstart sendt, ledd igjen). */
  showPlanned: boolean;
  /** Ad hoc-mellomfaktura er tillatt (oppstart passert, ikke sluttfakturert, noe gjenstår). */
  showAdHoc: boolean;
  onOpenCreate: (prefill: InvoicePrefill) => void;
}

/**
 * VIB-686/2836: mellomfakturaer på Faktura-fanen. Foreslår neste planlagte ledd
 * (kappet til det som gjenstår) og lar PL lage en ad hoc-mellomfaktura utenfor
 * planen, som så legges inn i planen.
 */
export function InterimInvoiceSection({
  projectName,
  calc,
  showPlanned,
  showAdHoc,
  onOpenCreate,
}: InterimInvoiceSectionProps) {
  const [adHocOpen, setAdHocOpen] = useState(false);
  const next = calc.nextMilestone;
  if (!(showPlanned && next) && !showAdHoc) return null;

  const termsAfterNext = calc.milestoneTerms.length - calc.nextMilestoneIndex - 1;

  return (
    <div className="flex flex-col gap-2 p-4 rounded-xl border-2 border-blue-300 bg-blue-50 text-blue-900">
      {showPlanned && next ? (
        <>
          <div className="flex items-start gap-3">
            <SrIcon name="receipt" className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">Mellomfaktura: {next.label}</p>
              <p className="text-xs text-blue-800 mt-1">
                Neste ledd i faktureringsplanen ({next.pct} %) på {formatNOK(calc.nextMilestoneAmount)}.{" "}
                {termsAfterNext} mellomfakturaer gjenstår etter denne.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="default"
              className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() =>
                onOpenCreate({
                  amount: calc.nextMilestoneAmount,
                  description: `${next.label} ${projectName} (${next.pct}%)`,
                  type: "interim",
                  milestone_index: calc.nextMilestoneIndex,
                  maxAmount: calc.remainingContract,
                })
              }
            >
              Opprett mellomfaktura ({formatNOK(calc.nextMilestoneAmount)})
            </Button>
            {showAdHoc && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAdHocOpen(true)}>
                Ny mellomfaktura utenfor planen
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm">
            Trenger kunden en faktura før sluttfakturaen? Lag en mellomfaktura på et valgfritt beløp.
          </p>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAdHocOpen(true)}>
            Ny mellomfaktura
          </Button>
        </div>
      )}

      {adHocOpen && (
        <AdHocInterimDialog
          contractAmount={calc.contractAmount}
          remaining={calc.remainingContract}
          onCancel={() => setAdHocOpen(false)}
          onContinue={(amount, pct) => {
            setAdHocOpen(false);
            onOpenCreate({
              amount,
              description: `Mellomfaktura ${projectName} (${pct}%)`,
              type: "interim",
              milestone_index: calc.nextMilestoneIndex,
              insertIntoPlan: true,
              maxAmount: calc.remainingContract,
            });
          }}
        />
      )}
    </div>
  );
}
